import {
  Flex,
  Loader,
  Title,
  Text,
  Paper,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import ReactECharts from "echarts-for-react";
import { IconEyeBolt } from "@tabler/icons-react";
import React, { useState, useMemo, useCallback } from "react";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import { NON_GOAL_BUCKETS } from "utils/strategicGoals";

const GOAL_COLOR_PALETTE = [
  "#4dabf7", // blue-5
  "#63e6be", // teal-4
  "#fcc419", // yellow-5
  "#f783ac", // pink-4
  "#94d82d", // lime-4
  "#22b8cf", // cyan-6
  "#845ef7", // violet-6
  "#ffa94d", // orange-4
  "#69db7c", // green-4
  "#748ffc", // indigo-4
];

const colorFromString = (str, palette) => {
  let hash = 0;
  for (let i = 0; i < (str || "").length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  const idx = Math.abs(hash) % palette.length;
  return palette[idx];
};

const GoalSplitCard = ({
  goals,
  isLoading,
  isError,
  source = "myTime",
  duration,
  self,
  uid,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { openDrilldown } = useInsightsDrilldown();
  const theme = useMantineTheme();
  const resolveMantineHex = (name) => {
    if (!name) return undefined;
    const key = String(name).toLowerCase();
    return theme.colors[key][6];
  };
  const handlePieClick = useCallback(
    (params) => {
      if (!params || params.name === "No goals") return;

      // Category level: drill into category goals
      if (!selectedCategory) {
        setSelectedCategory(params.name);
        return;
      }

      // Goal level: open the drill-down for the clicked goal
      const collection =
        source === "myOrg" ? "orgCalculations" : "userCalculations";
      openDrilldown({
        name: params.name,
        type: "Goals",
        redisKey:
          uid && duration && self
            ? `users:${uid}:${collection}:strategicGoals:${duration}:${self}:${params.name}`
            : null,
      });
    },
    [selectedCategory, openDrilldown, source, uid, duration, self]
  );

  const onEvents = useMemo(() => {
    return { click: handlePieClick };
  }, [handlePieClick]);

  // Build category -> color hex map from provided categoryColor or deterministic fallback
  const categoryColorHexMap = useMemo(() => {
    const mapping = {};
    Object.entries(goals || {}).forEach(([goalName, goalInfo]) => {
      const categoryName = goalInfo?.category || "Uncategorized";
      if (mapping[categoryName]) return;
      const provided = goalInfo?.categoryColor;
      const resolved = resolveMantineHex(provided);
      mapping[categoryName] =
        resolved || colorFromString(categoryName, GOAL_COLOR_PALETTE);
    });
    return mapping;
  }, [goals]);

  // Aggregates for drilldown
  const categoriesData = useMemo(() => {
    const categoryTotals = {};
    Object.entries(goals || {}).forEach(([goalName, goalInfo]) => {
      const categoryName = goalInfo?.category || "Uncategorized";
      const count = goalInfo?.count || 0;
      if (!categoryTotals[categoryName]) {
        categoryTotals[categoryName] = 0;
      }
      categoryTotals[categoryName] += count;
    });
    return Object.entries(categoryTotals)
      .map(([categoryName, total]) => ({
        name: categoryName,
        value: total,
        groupId: categoryName,
        itemStyle: {
          color:
            categoryColorHexMap[categoryName] ||
            colorFromString(categoryName, GOAL_COLOR_PALETTE),
        },
      }))
      .sort((a, b) => b.value - a.value);
  }, [goals, categoryColorHexMap]);

  const goalsByCategory = useMemo(() => {
    const mapping = {};
    Object.entries(goals || {}).forEach(([goalName, goalInfo]) => {
      const categoryName = goalInfo?.category || "Uncategorized";
      const count = goalInfo?.count || 0;
      if (!mapping[categoryName]) mapping[categoryName] = [];
      mapping[categoryName].push({ name: goalName, value: count });
    });
    Object.keys(mapping).forEach((cat) => {
      mapping[cat].sort((a, b) => b.value - a.value);
    });
    return mapping;
  }, [goals]);

  const chartData = useMemo(() => {
    if (selectedCategory) {
      const items = goalsByCategory[selectedCategory] || [];
      const categoryHex =
        categoryColorHexMap[selectedCategory] ||
        colorFromString(selectedCategory, GOAL_COLOR_PALETTE);
      if (items.length === 1) {
        return items.map((g) => ({
          ...g,
          groupId: selectedCategory,
          itemStyle: { color: categoryHex },
        }));
      }
      return items.map((g, idx) => ({
        ...g,
        groupId: selectedCategory,
        itemStyle: {
          color: GOAL_COLOR_PALETTE[idx % GOAL_COLOR_PALETTE.length],
        },
      }));
    }
    return categoriesData;
  }, [selectedCategory, goalsByCategory, categoriesData, categoryColorHexMap]);

  const option = {
    tooltip: {
      trigger: "item",
      formatter: (params) => {
        const name = params?.name ?? "";
        const value = params?.value ?? 0;
        const percent = Math.ceil(params?.percent ?? 0);
        return `${name}: ${value} meetings (${percent}%)`;
      },
      z: 9999,
      confine: false,
      appendToBody: true,
      enterable: true,
      hideDelay: 100,
    },
    series: [
      {
        id: "drilldownDonut",
        name: "Strategic Focus",
        type: "pie",
        radius: ["40%", "70%"],
        universalTransition: true,
        animation: true,
        animationDuration: 1400,
        animationDurationUpdate: 1400,
        animationEasing: "cubicOut",
        animationEasingUpdate: "cubicOut",
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: "#fff",
          borderWidth: 2,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        data: chartData,
      },
    ],
  };

  if (isLoading) {
    return (
      <Wrapper>
        <div className="loader">
          <Loader size="sm" />
          <Text mt={8} size="sm" color="dimmed">
            Fetching goal split
          </Text>
        </div>
      </Wrapper>
    );
  }
  if (isError) {
    return (
      <Wrapper>
        <div className="loader">
          <Text color="red" size="sm">
            Error while fetching goal split
          </Text>
        </div>
      </Wrapper>
    );
  }

  // "No goal" and "Others" are charted like goals so the pie covers the whole
  // calendar, but neither is goal coverage: an org whose meetings are all
  // untagged still has nothing to show, and should keep seeing the empty state
  // rather than a pie made entirely of the two catch-all slices.
  const hasGoals = Object.keys(goals || {}).some(
    (name) => !NON_GOAL_BUCKETS.includes(name)
  );
  return (
    <Wrapper>
      {selectedCategory ? (
        <Flex justify="space-between" align="center" mb={8}>
          <Text
            size="sm"
            color="dimmed"
            style={{ cursor: "pointer" }}
            onClick={() => setSelectedCategory(null)}
          >
            ← Back to categories
          </Text>
          <Text size="sm" weight={500}>
            {selectedCategory}
          </Text>
        </Flex>
      ) : null}
      {hasGoals ? (
        <ReactECharts
          option={option}
          style={{
            height: "240px",
            width: "100%",
            alignSelf: "center",
            overflow: "visible",
          }}
          opts={{ renderer: "svg" }}
          onEvents={onEvents}
        />
      ) : (
        <div
          style={{
            height: "240px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text size="sm" color="dimmed" align="center">
            No strategic goals found for this period
          </Text>
        </div>
      )}

      {hasGoals ? (
        <div className="stats">
          <Divider my="sm" />
          <Text size="xs" color="dimmed" mb={8}>
            {selectedCategory
              ? `Top Goals in ${selectedCategory}`
              : "Top Categories"}
          </Text>
          {selectedCategory
            ? (goalsByCategory[selectedCategory] || []).slice(0, 3).map((g) => (
                <Flex key={g.name} justify="space-between" mb={5}>
                  <Text size="sm">{g.name}</Text>
                  <Text size="sm" weight={500}>
                    {g.value} meetings
                  </Text>
                </Flex>
              ))
            : categoriesData.slice(0, 3).map((c) => (
                <Flex key={c.name} justify="space-between" mb={5}>
                  <Text size="sm">{c.name}</Text>
                  <Text size="sm" weight={500}>
                    {c.value} meetings
                  </Text>
                </Flex>
              ))}
        </div>
      ) : null}
    </Wrapper>
  );
};

export default GoalSplitCard;

const Wrapper = ({ children }) => {
  return (
    <Paper
      shadow="sm"
      p="md"
      radius="md"
      withBorder
      className="card"
      style={{ overflow: "visible", flexDirection: "column" }}
      h={"100%"}
    >
      <Flex direction="column" sx={{ width: "100%", flex: 1 }}>
        <Flex align="center">
          <IconEyeBolt size={18} style={{ marginRight: 8 }} />
          <Title order={5}>Strategic Focus</Title>
        </Flex>
        <Flex direction="column" style={{ flex: 1 }}>
          {children}
        </Flex>
      </Flex>
    </Paper>
  );
};
